/*
 * Code generated by Speakeasy (https://speakeasy.com). DO NOT EDIT.
 */

import * as z from "zod/v3";
import { safeParse } from "../../lib/schemas.js";
import { Result as SafeParseResult } from "../../types/fp.js";
import { SDKValidationError } from "../errors/sdkvalidationerror.js";
import {
  BillableFee,
  BillableFee$inboundSchema,
  BillableFee$Outbound,
  BillableFee$outboundSchema,
} from "./billablefee.js";
import {
  CardAcquiringModel,
  CardAcquiringModel$inboundSchema,
  CardAcquiringModel$outboundSchema,
} from "./cardacquiringmodel.js";
import {
  FeePlanAgreementStatus,
  FeePlanAgreementStatus$inboundSchema,
  FeePlanAgreementStatus$outboundSchema,
} from "./feeplanagreementstatus.js";
import {
  MinimumCommitment,
  MinimumCommitment$inboundSchema,
  MinimumCommitment$Outbound,
  MinimumCommitment$outboundSchema,
} from "./minimumcommitment.js";
import {
  MonthlyPlatformFee,
  MonthlyPlatformFee$inboundSchema,
  MonthlyPlatformFee$Outbound,
  MonthlyPlatformFee$outboundSchema,
} from "./monthlyplatformfee.js";

export type PartnerPricingAgreement = {
  agreementID: string;
  planID: string;
  accountID?: string | undefined;
  /**
   * The name of the agreement.
   */
  name: string;
  /**
   * The description of the agreement.
   */
  description?: string | undefined;
  acceptedOn: Date;
  status: FeePlanAgreementStatus;
  /**
   * Specifies the card processing pricing model
   */
  cardAcquiringModel: CardAcquiringModel;
  billableFees: Array<BillableFee>;
  /**
   * The minimum spending amount that must be met in the billing period. If actual usage is below the minimum amount, account is charged the difference.
   */
  minimumCommitment: MinimumCommitment;
  /**
   * Fixed recurring amount paid in the billing period regardless of usage.
   */
  monthlyPlatformFee: MonthlyPlatformFee;
  /**
   *   The decimal-formatted numerical string of the revenue split for partner.
   *
   * @remarks
   *
   *   For example, 2.25% is '2.25'.
   */
  revenueShare: string;
};

/** @internal */
export const PartnerPricingAgreement$inboundSchema: z.ZodType<
  PartnerPricingAgreement,
  z.ZodTypeDef,
  unknown
> = z.object({
  agreementID: z.string(),
  planID: z.string(),
  accountID: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  acceptedOn: z.string().datetime({ offset: true }).transform(v => new Date(v)),
  status: FeePlanAgreementStatus$inboundSchema,
  cardAcquiringModel: CardAcquiringModel$inboundSchema,
  billableFees: z.array(BillableFee$inboundSchema),
  minimumCommitment: MinimumCommitment$inboundSchema,
  monthlyPlatformFee: MonthlyPlatformFee$inboundSchema,
  revenueShare: z.string(),
});
/** @internal */
export type PartnerPricingAgreement$Outbound = {
  agreementID: string;
  planID: string;
  accountID?: string | undefined;
  name: string;
  description?: string | undefined;
  acceptedOn: string;
  status: string;
  cardAcquiringModel: string;
  billableFees: Array<BillableFee$Outbound>;
  minimumCommitment: MinimumCommitment$Outbound;
  monthlyPlatformFee: MonthlyPlatformFee$Outbound;
  revenueShare: string;
};

/** @internal */
export const PartnerPricingAgreement$outboundSchema: z.ZodType<
  PartnerPricingAgreement$Outbound,
  z.ZodTypeDef,
  PartnerPricingAgreement
> = z.object({
  agreementID: z.string(),
  planID: z.string(),
  accountID: z.string().optional(),
  name: z.string(),
  description: z.string().optional(),
  acceptedOn: z.date().transform(v => v.toISOString()),
  status: FeePlanAgreementStatus$outboundSchema,
  cardAcquiringModel: CardAcquiringModel$outboundSchema,
  billableFees: z.array(BillableFee$outboundSchema),
  minimumCommitment: MinimumCommitment$outboundSchema,
  monthlyPlatformFee: MonthlyPlatformFee$outboundSchema,
  revenueShare: z.string(),
});

export function partnerPricingAgreementToJSON(
  partnerPricingAgreement: PartnerPricingAgreement,
): string {
  return JSON.stringify(
    PartnerPricingAgreement$outboundSchema.parse(partnerPricingAgreement),
  );
}
export function partnerPricingAgreementFromJSON(
  jsonString: string,
): SafeParseResult<PartnerPricingAgreement, SDKValidationError> {
  return safeParse(
    jsonString,
    (x) => PartnerPricingAgreement$inboundSchema.parse(JSON.parse(x)),
    `Failed to parse 'PartnerPricingAgreement' from JSON`,
  );
}
